import React from 'react';
import { IS_T3_WEBSITE } from '../../constants/environment';
import LightBox from '../LightBox';
import VehicleInfo from '../VehicleInfo';
import VehicleMileageInfo from '../VehicleMileageInfo';
import VehicleStatusInfo from '../VehicleStatusInfo';
import {
  VehicleHeaderWrapper,
  HeadingContainerNonAudi,
  HeaderTextContainer,
  BackgroundImage,
  ImageMobileUsed,
  ImpelCTAWrapper,
  TabletSubHeader,
  HeaderUsed,
  Image,
} from './VehicleHeader.style';
import type { VehicleInfo as VehicleInfoTypes, Impel360ViewCTA } from '../../types/index';
import useOptimizePrimaryImage from '../../hooks/useOptimizePrimaryImage';
import usePalette from '../../hooks/usePalette';
import usePrimaryImageUrl from '../../hooks/usePrimaryImageUrl';
import isFallback from '../../utils/isFallback';
import Impel360CTA from '../Impel360CTA';

interface HeaderUsedProps {
  vehicleData?: VehicleInfoTypes;
  cta360View: Impel360ViewCTA;
  isTablet: boolean;
  isMobile: boolean;
  loading: boolean;
}

const HeaderUsedWrapper: React.FC<HeaderUsedProps> = ({
  vehicleData,
  cta360View,
  isTablet,
  isMobile,
  loading,
}) => {
  let primaryImageUrl: string = usePrimaryImageUrl(vehicleData);

  const { backgroundColor, darkenedBackgroundColor, textColor } = usePalette(vehicleData);

  const vehicleType = vehicleData?.vehicleType;
  const modelName = vehicleData?.modelName;
  const nonAudi = vehicleType === 'non-audi';
  const isUsed = vehicleType === 'used';
  const isNew = vehicleType === 'new';

  primaryImageUrl = useOptimizePrimaryImage(primaryImageUrl, isNew);
  const fallback = primaryImageUrl ? isFallback(primaryImageUrl) : false;

  const usedImages = vehicleData?.vehicleImages;
  const images = vehicleData?.vehicleMedia.mediaImages;

  const { is360ViewEnabled, cta360ViewButtonLabel } = cta360View;

  return (
    <>
      <HeaderUsed
        color={nonAudi ? '#ffffff' : textColor}
        fallback={fallback}
        backgroundColor={nonAudi ? '#666666' : backgroundColor}
        darkenedBackgroundColor={darkenedBackgroundColor}
        nonAudi={nonAudi}
        mediaImages={vehicleData?.vehicleImages ?? ['']}
        data-testid="header"
      >
        <VehicleHeaderWrapper isUsed={isUsed} fallback={fallback}>
          <HeadingContainerNonAudi backgroundColor={nonAudi ? '#666666' : backgroundColor}>
            <HeaderTextContainer
              nonAudi={nonAudi}
              isUsed={isUsed}
              fallback={fallback}
              data-testid="vehicle-info"
            >
              <div
                data-vehicle-status={vehicleData?.vehicleOrderStatus || 'Unavailable'}
                data-vehicle-sale-type={
                  vehicleData?.saleOrderType || vehicleData?.vehicleInventoryType
                }
              >
                <VehicleInfo />
                {IS_T3_WEBSITE && <VehicleStatusInfo />}
              </div>
              <ImpelCTAWrapper>
                {!loading && <VehicleMileageInfo />}{' '}
                <Impel360CTA
                  isVisible={is360ViewEnabled}
                  label={cta360ViewButtonLabel}
                  color={nonAudi ? '#ffffff' : textColor}
                  isNew={isNew}
                  isT3={IS_T3_WEBSITE}
                />
              </ImpelCTAWrapper>
            </HeaderTextContainer>
            {!isTablet && !isMobile && primaryImageUrl && (
              <BackgroundImage
                image={primaryImageUrl}
                nonAudi={nonAudi}
                fallback={fallback}
                data-testid="primary-image"
              />
            )}
            {isMobile && primaryImageUrl && (
              <ImageMobileUsed src={primaryImageUrl} data-testid="primary-image" alt={modelName} />
            )}
          </HeadingContainerNonAudi>
        </VehicleHeaderWrapper>

        {!isTablet && (
          <LightBox
            usedImages={usedImages}
            images={images}
            header={primaryImageUrl}
            modelName={modelName}
            nonAudi={nonAudi}
            used={isUsed}
          />
        )}
      </HeaderUsed>
      {isTablet && (
        <TabletSubHeader>
          {primaryImageUrl && <Image src={primaryImageUrl} alt={modelName} />}
          <LightBox
            usedImages={usedImages}
            images={images}
            header={primaryImageUrl}
            modelName={modelName}
            nonAudi={nonAudi}
            used={isUsed}
          />
        </TabletSubHeader>
      )}
    </>
  );
};

export default HeaderUsedWrapper;
